import {
  getApiNotificationsV1PreferencesRetrieveQueryKey,
  type SimpleUserNotificationPreference,
  type UserNotificationPreferenceUpdate,
  useApiNotificationsV1PreferencesUpdate,
  useQueryClient,
} from 'api';

export const getOptimisticNotificationPreference = ({
  current,
  data,
}: {
  current: SimpleUserNotificationPreference | undefined;
  data: UserNotificationPreferenceUpdate;
}): SimpleUserNotificationPreference => {
  const nextEmailEnabled = data.email_enabled ?? current?.email_enabled ?? true;
  const nextDailyQualityDigestEnabled =
    data.daily_quality_digest_enabled ?? current?.daily_quality_digest_enabled ?? false;
  const nextWeeklyQualityDigestEnabled =
    data.weekly_quality_digest_enabled ?? current?.weekly_quality_digest_enabled ?? false;
  const nextNotifyBelowSpecLoadEnabled =
    data.notify_below_spec_load_enabled ?? current?.notify_below_spec_load_enabled ?? false;
  const nextNotifyBuyerEvidenceRequestCompletedEnabled =
    data.notify_buyer_evidence_request_completed_enabled ??
    current?.notify_buyer_evidence_request_completed_enabled ??
    false;
  const nextDailyProductionSummaryEnabled =
    data.daily_production_summary_enabled ?? current?.daily_production_summary_enabled ?? false;

  return {
    daily_production_summary_enabled: nextEmailEnabled && nextDailyProductionSummaryEnabled,
    daily_quality_digest_enabled: nextEmailEnabled && nextDailyQualityDigestEnabled,
    email_enabled: nextEmailEnabled,
    notify_below_spec_load_enabled: nextEmailEnabled && nextNotifyBelowSpecLoadEnabled,
    notify_buyer_evidence_request_completed_enabled: nextEmailEnabled && nextNotifyBuyerEvidenceRequestCompletedEnabled,
    weekly_quality_digest_enabled: nextEmailEnabled && nextWeeklyQualityDigestEnabled,
  };
};

export const useUpdateNotificationPreference = () => {
  const queryClient = useQueryClient();
  const notificationPreferenceQueryKey = getApiNotificationsV1PreferencesRetrieveQueryKey();
  const { mutate: updateNotificationPreference } = useApiNotificationsV1PreferencesUpdate({
    mutation: {
      onError: (_error, _variables, previousNotificationPreference) => {
        if (previousNotificationPreference) {
          queryClient.setQueryData(notificationPreferenceQueryKey, previousNotificationPreference);
        }
      },
      onMutate: async ({ data }) => {
        await queryClient.cancelQueries({
          queryKey: notificationPreferenceQueryKey,
        });
        const previousNotificationPreference =
          queryClient.getQueryData<SimpleUserNotificationPreference>(notificationPreferenceQueryKey);

        queryClient.setQueryData<SimpleUserNotificationPreference>(notificationPreferenceQueryKey, (current) =>
          getOptimisticNotificationPreference({ current, data: data ?? {} })
        );

        return previousNotificationPreference;
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: notificationPreferenceQueryKey,
        });
      },
    },
  });

  return updateNotificationPreference;
};
